import { useEffect, useState, type FormEvent } from 'react'
import { useAuth } from '../auth/AuthContext'
import { ApiError } from '../api/client'
import { Button } from './ui/Button'
import { Input } from './ui/Input'
import { ErrorBanner } from './ErrorBanner'

// Client-side resend cooldown, aligned with the server's 60s per-account
// cooldown (PASSWORD_RESET_RESEND_COOLDOWN). The server swallows an early resend
// silently (it never reveals whether the email has an account), so the button
// must enforce the wait itself.
const RESEND_COOLDOWN_SECONDS = 60
// Reset codes are 8 digits (password_reset.go) — longer than the 6-digit
// email-verification code because a reset code can take over the account.
const RESET_CODE_LENGTH = 8
// Matches the server floor and the signup form's minLength.
const MIN_PASSWORD_LENGTH = 8

// resetErrorMessage maps the owner-plane forgot-password error codes
// (password_reset.go — not in the SDK BacklitError allowlist) to friendly copy.
function resetErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.code) {
      case 'invalid_code':
        return 'That code is incorrect or has expired. Check it, or request a new one.'
      case 'weak_password':
        return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
      case 'invalid_email':
        return 'Enter a valid email address.'
      case 'too_many_requests':
        return 'Too many requests — please wait a moment and try again.'
      case 'email_send_failed':
        return 'We couldn’t send the code. Please try again.'
      case 'password_reset_unavailable':
        return 'Password reset isn’t available right now. Try signing in with Google or Microsoft, or try again later.'
    }
    return e.message
  }
  return 'Something went wrong. Please try again.'
}

// PasswordResetForm is the signed-out forgot-password flow on the Login card.
// Step 1 asks for the account email and requests a code; step 2 takes the
// 8-digit code plus a new password, and on success AuthContext signs straight in
// with the new password (Login's redirect then takes over).
export function PasswordResetForm({
  initialEmail = '',
  onBack,
}: {
  // Prefilled from whatever the user already typed on the sign-in form.
  initialEmail?: string
  // Return to the sign-in form.
  onBack: () => void
}) {
  const auth = useAuth()
  const [step, setStep] = useState<'request' | 'confirm'>('request')
  const [email, setEmail] = useState(initialEmail)
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [cooldown, setCooldown] = useState(0)

  // Tick the resend cooldown down to zero.
  useEffect(() => {
    if (cooldown <= 0) return
    const t = setInterval(() => setCooldown((c) => (c <= 1 ? 0 : c - 1)), 1000)
    return () => clearInterval(t)
  }, [cooldown])

  const trimmedEmail = email.trim()

  const requestCode = async () => {
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      await auth.sendPasswordReset(trimmedEmail)
      setStep('confirm')
      setNotice(
        `If ${trimmedEmail} has a Backlit password, we’ve sent it an ${RESET_CODE_LENGTH}-digit code. It expires in 10 minutes.`,
      )
      setCooldown(RESEND_COOLDOWN_SECONDS)
    } catch (e) {
      setError(resetErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const submitRequest = (e: FormEvent) => {
    e.preventDefault()
    void requestCode()
  }

  const submitReset = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setNotice(null)
    if (password !== confirmPassword) {
      setError('Those passwords don’t match.')
      return
    }
    setBusy(true)
    try {
      await auth.confirmPasswordReset(trimmedEmail, code, password)
      // Signed in — Login's auth.user redirect unmounts this form.
    } catch (err) {
      if (err instanceof ApiError) {
        setError(resetErrorMessage(err))
      } else {
        // The reset landed but the follow-up Firebase signin didn't.
        setError('Your password was reset, but we couldn’t sign you in. Go back and sign in with your new password.')
      }
    } finally {
      setBusy(false)
    }
  }

  const backLink = (
    <button type="button" className="font-medium text-glow-600 hover:underline" onClick={onBack}>
      Back to sign in
    </button>
  )

  if (step === 'request') {
    return (
      <form className="space-y-3" onSubmit={submitRequest}>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <p className="text-sm text-cream/70">
          Enter the email you sign in with and we’ll send you a code to choose a new password.
        </p>
        <Input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
          autoFocus
        />
        <Button type="submit" className="w-full" disabled={busy || trimmedEmail === ''}>
          {busy ? 'Sending…' : 'Send reset code'}
        </Button>
        <p className="text-center text-sm text-cream/70">{backLink}</p>
      </form>
    )
  }

  return (
    <form className="space-y-3" onSubmit={submitReset}>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      {notice && !error && (
        <p role="status" className="text-sm text-glow-200">
          {notice}
        </p>
      )}
      <Input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        maxLength={RESET_CODE_LENGTH}
        placeholder="12345678"
        aria-label={`${RESET_CODE_LENGTH}-digit reset code`}
        value={code}
        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
        required
        autoFocus
      />
      <Input
        type="password"
        placeholder="New password"
        aria-label="New password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
        autoComplete="new-password"
        minLength={MIN_PASSWORD_LENGTH}
      />
      <Input
        type="password"
        placeholder="Confirm new password"
        aria-label="Confirm new password"
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        required
        autoComplete="new-password"
        minLength={MIN_PASSWORD_LENGTH}
      />
      <Button
        type="submit"
        className="w-full"
        disabled={busy || code.length !== RESET_CODE_LENGTH || password.length < MIN_PASSWORD_LENGTH}
      >
        {busy ? 'Resetting…' : 'Reset password'}
      </Button>
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <button
          type="button"
          className="font-medium text-glow-600 hover:underline disabled:opacity-50"
          disabled={busy || cooldown > 0}
          onClick={() => void requestCode()}
        >
          {cooldown > 0 ? `Resend code (${cooldown}s)` : 'Resend code'}
        </button>
        <button
          type="button"
          className="font-medium text-glow-600 hover:underline"
          onClick={() => {
            setStep('request')
            setCode('')
            setError(null)
            setNotice(null)
          }}
        >
          Use a different email
        </button>
      </div>
      <p className="text-center text-sm text-cream/70">{backLink}</p>
    </form>
  )
}
